import type { ViewCondition, ConditionOperator } from '../models/ViewRules';

interface CustomField {
  id: string;
  name: string;
  type: string;
  value?: string | number;
}

export interface NodeData {
  id: string;
  name?: string;
  attrs?: Record<string, unknown>;
  props?: Record<string, unknown>;
  [key: string]: unknown;
}

// --- Pluggable field normalizer (domain-agnostic hook) ---
type FieldNormalizerFn = (fieldId: string) => string;
let _fieldNormalizer: FieldNormalizerFn | null = null;

/**
 * Register a domain-specific field name normalizer.
 * Called once during domain initialization (e.g., orgchart registers its alias-based normalizer).
 */
export const setFieldNormalizer = (fn: FieldNormalizerFn): void => {
  _fieldNormalizer = fn;
};

/**
 * Normalize a field ID using the registered normalizer, if any.
 * Returns the input unchanged if no normalizer is registered.
 */
export const normalizeField = (fieldId: string): string => {
  return _fieldNormalizer ? _fieldNormalizer(fieldId) : fieldId;
};

const _hasValue = (v: unknown): boolean => v !== undefined && v !== null && v !== '';

const _isEmptyConditionValue = (v: ViewCondition['value']): boolean => {
  if (v === null || v === undefined || v === '') return true;
  return Array.isArray(v) && v.length === 0;
};

/**
 * Resolves field values from nested node data structure.
 * Supports:
 * - Direct properties: node.name, node.title
 * - attrs properties: node.attrs.name, node.attrs.title
 * - props properties: node.props.name, node.props.title
 * - Custom fields: node.attrs.customFields[].value (matched by id or name)
 * - Fuzzy fallback via registered normalizer (alias-based resolution)
 */
export const resolveNodeField = (node: NodeData, fieldId: string): unknown => {
  const attrs = (node.attrs || {}) as Record<string, unknown>;
  const props = (node.props || {}) as Record<string, unknown>;
  const customFields = (attrs.customFields || props.customFields || []) as CustomField[];

  // Standard fields - check attrs first, then props, then root node
  const standardFields = ['name', 'title', 'role', 'department', 'location', 'email', 'phone', 'personId'];

  if (standardFields.includes(fieldId)) {
    if (_hasValue(attrs[fieldId])) return attrs[fieldId];
    if (_hasValue(props[fieldId])) return props[fieldId];
    if (_hasValue(node[fieldId])) return node[fieldId];
    return null;
  }

  // Computed/special fields
  if (fieldId === 'tenure') {
    const joinDate = attrs.joinDate || attrs.startDate || props.joinDate || props.startDate;
    if (joinDate && typeof joinDate === 'string') {
      const joined = new Date(joinDate);
      const now = new Date();
      const years = (now.getTime() - joined.getTime()) / (365.25 * 24 * 60 * 60 * 1000);
      return Math.max(0, Math.floor(years));
    }
    return null;
  }

  // Date fields — return raw date string (heatmap/filter can convert)
  if (fieldId === 'startDate' || fieldId === 'joinDate') {
    const val = attrs.joinDate || attrs.startDate || props.joinDate || props.startDate;
    return val ?? null;
  }

  // Custom fields - try by id first, then by name
  if (Array.isArray(customFields)) {
    const byId = customFields.find((f) => f.id === fieldId);
    if (byId) return byId.value;

    const byName = customFields.find(
      (f) => f.name?.toLowerCase() === fieldId.toLowerCase()
    );
    if (byName) return byName.value;
  }

  // Fall back to checking attrs and props directly for any other field
  if (attrs[fieldId] !== undefined) return attrs[fieldId];
  if (props[fieldId] !== undefined) return props[fieldId];
  if (node[fieldId] !== undefined) return node[fieldId];

  // --- Fuzzy fallback via registered normalizer ---
  if (_fieldNormalizer) {
    const canonical = _fieldNormalizer(fieldId);

    // Case A: fieldId is a variant (e.g., "pay_band"), data stored under canonical key ("band")
    if (canonical !== fieldId) {
      if (standardFields.includes(canonical)) {
        if (_hasValue(attrs[canonical])) return attrs[canonical];
        if (_hasValue(props[canonical])) return props[canonical];
        if (_hasValue(node[canonical])) return node[canonical];
      }
      if (_hasValue(attrs[canonical])) return attrs[canonical];
      if (_hasValue(props[canonical])) return props[canonical];
      if (Array.isArray(customFields)) {
        const byId = customFields.find((f) => f.id === canonical);
        if (byId) return byId.value;
      }
    }

    // Case B: fieldId is canonical (e.g., "band"), data stored under variant key ("payBand")
    for (const key of Object.keys(attrs)) {
      if (key === fieldId || key === canonical) continue;
      if (_fieldNormalizer(key) === canonical && _hasValue(attrs[key])) {
        return attrs[key];
      }
    }
    for (const key of Object.keys(props)) {
      if (key === fieldId || key === canonical) continue;
      if (_fieldNormalizer(key) === canonical && _hasValue(props[key])) {
        return props[key];
      }
    }
  }

  return null;
};

/**
 * Evaluate a single condition against a node
 */
export const evaluateCondition = (node: NodeData, condition: ViewCondition): boolean => {
  // Skip conditions with no value (except is_empty/is_not_empty)
  if (_isEmptyConditionValue(condition.value)) {
    if (condition.operator !== 'is_empty' && condition.operator !== 'is_not_empty') {
      return true; // Partially configured condition - don't filter
    }
  }

  const fieldValue = resolveNodeField(node, condition.field);
  const { operator, value } = condition;

  const stringValue = fieldValue != null ? String(fieldValue) : '';
  const compareValues = Array.isArray(value)
    ? value.map((item) => String(item))
    : [value != null ? String(value) : ''];
  const compareValue = compareValues[0] ?? '';

  const operatorFunctions: Record<ConditionOperator, () => boolean> = {
    equals: () => compareValues.includes(stringValue),
    not_equals: () => !compareValues.includes(stringValue),
    contains: () => stringValue.toLowerCase().includes(compareValue.toLowerCase()),
    not_contains: () => !stringValue.toLowerCase().includes(compareValue.toLowerCase()),
    starts_with: () => stringValue.toLowerCase().startsWith(compareValue.toLowerCase()),
    ends_with: () => stringValue.toLowerCase().endsWith(compareValue.toLowerCase()),
    greater_than: () => {
      const numA = parseFloat(stringValue);
      const numB = parseFloat(compareValue);
      return !isNaN(numA) && !isNaN(numB) && numA > numB;
    },
    less_than: () => {
      const numA = parseFloat(stringValue);
      const numB = parseFloat(compareValue);
      return !isNaN(numA) && !isNaN(numB) && numA < numB;
    },
    is_empty: () => fieldValue == null || stringValue === '',
    is_not_empty: () => fieldValue != null && stringValue !== '',
  };

  return operatorFunctions[operator]?.() ?? false;
};

/**
 * Evaluate multiple conditions with AND/OR logic
 */
export const evaluateConditions = (
  node: NodeData,
  conditions: ViewCondition[],
  logic: 'and' | 'or'
): boolean => {
  if (conditions.length === 0) return true;

  // Filter out incomplete conditions (no value, except is_empty/is_not_empty)
  const validConditions = conditions.filter((c) => {
    if (c.operator === 'is_empty' || c.operator === 'is_not_empty') return true;
    return !_isEmptyConditionValue(c.value);
  });

  if (validConditions.length === 0) return true;

  if (logic === 'and') {
    return validConditions.every((c) => evaluateCondition(node, c));
  } else {
    return validConditions.some((c) => evaluateCondition(node, c));
  }
};

/**
 * Resolve template variables in a label template string
 * e.g., "{{tenure}} years" -> "5 years"
 */
export const resolveTemplate = (template: string, node: NodeData): string => {
  return template.replace(/\{\{(\w+)\}\}/g, (_, field) => {
    const value = resolveNodeField(node, field);
    return value != null ? String(value) : '';
  });
};

export const CONDITION_OPERATOR_DEFAULT_LABELS: Record<ConditionOperator, string> = {
  equals: 'equals',
  not_equals: 'not equals',
  contains: 'contains',
  not_contains: 'does not contain',
  starts_with: 'starts with',
  ends_with: 'ends with',
  greater_than: 'greater than',
  less_than: 'less than',
  is_empty: 'is empty',
  is_not_empty: 'is not empty',
};

type DiagramTranslate = (key: string, options?: { defaultValue?: string }) => unknown;

export function conditionOperatorLabel(t: DiagramTranslate, operator: ConditionOperator): string {
  return t(`assistant.operators.${operator}`, {
    defaultValue: CONDITION_OPERATOR_DEFAULT_LABELS[operator],
  }) as string;
}
